import { FeatureFlags } from 'js/sign-components/common/feature-flags';
import type { HSUser } from 'hellospa/common/hs-user';
import type { SiteCode } from 'js/sign-components/generated/types/WebApp';
import type { TourName } from 'js/sign-components/common/TourName';
import type { SigData } from 'signer-app/context/signer-app-client/signature';
import type { GlobalPageProps } from 'hellospa/slices/page-props';
import invariant from 'invariant';

/**
 * JSON-parses the inner HTML of a node in the DOM.  Used for retrieving initial
 * client-side state rendered out by the server.
 */
export default function parseDomNodeContents(id: string): unknown {
  const element = document.getElementById(id);

  if (element && element.textContent !== null) {
    return JSON.parse(element.textContent);
  } else {
    throw new Error(`Could not parse contents of node with ID ${id}.`);
  }
}

/**
 * Initial state shape for the HelloSPA application, parsed from the
 * __root-props__ DOM node rendered by the PHP backend.
 */
export type RootProps = {
  csrfToken: string;
  user?: HSUser;
  featureFlags?: FeatureFlags;
  siteCode: SiteCode;
  isDropbox: boolean;
  isEmbedded: boolean;
  termsURL: string;
  privacyURL: string;
  stripePublishableKey?: string;
  signature?: SigData;
  preloadedTsmGroupKey: string;
  addBackground?: boolean;
  completedTours?: Record<string, TourName>;
  pageKey: GlobalPageProps['pageKey'];
  pageProps: any;
  locale: string;
  showTutorial?: boolean;
  enterpriseTutorial?: boolean;
  collectInfoForm?: boolean;
  baseUrl?: string;
};

/**
 * Extracts and parses the root props for HelloSPA from the __root-props__ DOM node.
 *
 * TODO: Add runtime type validation (like parseBundleProps) to verify all
 * required properties are present with correct types instead of just casting.
 */
export function extractRootPropsFromDom(): RootProps {
  const element = document.getElementById('__root-props__');

  if (element && element.textContent !== null) {
    return JSON.parse(element.textContent) as RootProps;
  } else {
    throw new Error('Could not parse contents of node with ID __root-props__.');
  }
}

export type BundleProps = {
  locale: string;
  csrfToken: string;
  siteCode: string;
  featureFlags: FeatureFlags;
  userGuid?: string;
};
export function parseBundleProps(): BundleProps {
  const id = '__bundle-props__';
  const el = document.getElementById(id);

  if (el) {
    const bundleProps = JSON.parse(el.innerHTML);
    invariant(typeof bundleProps === 'object', 'Expected to find an object');
    invariant(typeof bundleProps.locale === 'string', 'Missing locale');
    invariant(typeof bundleProps.csrfToken === 'string', 'Missing csrfToken');
    invariant(typeof bundleProps.siteCode === 'string', 'Missing siteCode');
    invariant(
      typeof bundleProps.featureFlags === 'object',
      'Missing featureFlags',
    );
    return bundleProps;
  } else {
    throw new Error(`Could not parse contents of node with ID ${id}.`);
  }
}
